import mqtt from "mqtt";
import { useState, useEffect, useRef, useCallback } from "react";

/**
 * Hook para recibir posiciones indoor en tiempo real via MQTT.
 * Suscribe a myindustrialdatagps_indoor/{projectId}.
 * Mensaje: { device_user_id, floor_id, position_x, position_y, confidence, timestamp }
 * @param {number} projectId - ID del proyecto
 * @returns {{ positions, loaded, reconnecting, getPosition }}
 */
export default function useMQTTIndoor(projectId) {
    const [client, setClient] = useState(null);
    const [reconnecting, setReconnecting] = useState(false);
    const [loaded, setLoaded] = useState(false);
    // positions: { device_user_id: { floor_id, position_x, position_y, confidence, timestamp } }
    const [positions, setPositions] = useState({});
    const subscribedTopics = useRef(new Set());

    useEffect(() => {
        if (!projectId) return;

        const brokerUrl = process.env.REACT_APP_MQTT_WS_URL;
        const username = process.env.REACT_APP_MQTT_USER;
        const password = process.env.REACT_APP_MQTT_PASSWORD;

        if (!brokerUrl) return;

        const mqttClient = mqtt.connect(brokerUrl, {
            username,
            password,
            clientId: `web_indoor_${Math.random().toString(16).substring(2, 10)}`,
            clean: true,
            reconnectPeriod: 5000,
            connectTimeout: 30000,
        });

        setClient(mqttClient);

        return () => {
            if (mqttClient) mqttClient.end();
        };
    }, [projectId]);

    useEffect(() => {
        if (!client || !projectId) return;

        const topic = `myindustrialdatagps_indoor/${projectId}`;

        const onConnect = () => {
            setReconnecting(false);
            setLoaded(true);
            if (!subscribedTopics.current.has(topic)) {
                client.subscribe(topic, { qos: 1 }, (err) => {
                    if (!err) subscribedTopics.current.add(topic);
                });
            }
        };

        const onMessage = (receivedTopic, message) => {
            if (!receivedTopic.startsWith('myindustrialdatagps_indoor/')) return;
            try {
                const parsed = JSON.parse(message.toString());
                const duId = parsed.device_user_id;
                if (duId) {
                    setPositions(prev => ({
                        ...prev,
                        [duId]: {
                            floor_id: parsed.floor_id,
                            position_x: parsed.position_x,
                            position_y: parsed.position_y,
                            confidence: parsed.confidence,
                            timestamp: parsed.timestamp,
                        }
                    }));
                }
            } catch (e) {
                console.error('MQTT Indoor: Error parsing message', e);
            }
        };

        const onError = () => setReconnecting(true);
        const onClose = () => setReconnecting(true);
        const onReconnect = () => {
            setReconnecting(true);
            subscribedTopics.current.clear();
        };

        client.on('connect', onConnect);
        client.on('message', onMessage);
        client.on('error', onError);
        client.on('close', onClose);
        client.on('reconnect', onReconnect);

        if (client.connected) onConnect();

        return () => {
            client.off('connect', onConnect);
            client.off('message', onMessage);
            client.off('error', onError);
            client.off('close', onClose);
            client.off('reconnect', onReconnect);
            subscribedTopics.current.forEach(t => client.unsubscribe(t));
            subscribedTopics.current.clear();
        };
    }, [client, projectId]);

    const getPosition = useCallback((deviceUserId) => {
        return positions[deviceUserId] || null;
    }, [positions]);

    return { positions, loaded, reconnecting, getPosition };
}
